import React, { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { ChoiceDialog, ChoiceOption } from "./ChoiceDialog";

/**
 * "Add Client" chooser, shared by the organisation page and the project page.
 *
 * Step 1 asks which kind of add this is:
 *   • Add an existing client → step 2, a picker listing viewer accounts only
 *   • Invite a new client    → hands off to the existing invite-client form
 *
 * Only viewers are ever listed, because a client is a viewer by definition —
 * a teammate must be added through the normal Add Member control instead.
 */

const UserPlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" /></svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
);

const AddClientQuestionModal = ({
  open,
  onClose,
  onInviteNew,
  onAddExisting,
  availableClients = [],
  isLoadingClients,
  isAdding,
  error,
}) => {
  const [mode, setMode] = useState("ask"); // "ask" | "existing"
  const [choice, setChoice] = useState("existing"); // "existing" | "invite"
  const [search, setSearch] = useState("");

  if (!open) return null;

  const close = () => {
    setMode("ask");
    setChoice("existing");
    setSearch("");
    onClose();
  };

  // Next Step does exactly what clicking a card used to do.
  const proceed = () => {
    if (choice === "invite") {
      close();
      onInviteNew();
    } else {
      setMode("existing");
    }
  };

  const term = search.toLowerCase();
  const filtered = (availableClients || []).filter(
    (c) =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term),
  );

  const errorBox = error && (
    <div className="mb-4 rounded-[10px] border border-red-200 bg-red-50 p-3 text-sm text-red-600">
      {error}
    </div>
  );

  if (mode === "ask") {
    return (
      <ChoiceDialog
        icon={<UserPlusIcon />}
        title="Add a Client"
        subtitle="Choose whether to link an existing client account or invite a new stakeholder."
        onClose={close}
        footerHint={
          <span className="flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
            You can modify client access anytime
          </span>
        }
        primaryLabel="Next Step"
        onPrimary={proceed}
      >
        {errorBox}
        <div role="radiogroup" className="space-y-3">
          <ChoiceOption
            selected={choice === "existing"}
            onSelect={() => setChoice("existing")}
            icon={<CheckCircleIcon />}
            title="Add Existing Client"
            tag="Fast link"
            description="Select from client accounts already registered in your workspace."
          />
          <ChoiceOption
            selected={choice === "invite"}
            onSelect={() => setChoice("invite")}
            icon={<SendIcon />}
            title="Invite New Client"
            tag="Email invite"
            description="Send an email invitation so a new client can set up their account and access."
          />
        </div>
      </ChoiceDialog>
    );
  }

  return (
    <ChoiceDialog
      icon={<CheckCircleIcon />}
      title="Add Existing Client"
      subtitle="Only client (viewer) accounts are listed."
      onClose={close}
    >
      {errorBox}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients"
          className="h-[42px] w-full rounded-[10px] border border-[#D0D5DD] bg-white pl-10 pr-3 text-sm outline-none focus:border-[#4F46E5]"
        />
      </div>

      <div className="max-h-[280px] overflow-y-auto rounded-[12px] border border-[#E4E7EC] bg-white">
        {isLoadingClients ? (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-[#98A2B3]">
            {(availableClients || []).length === 0
              ? "No client accounts available. Invite a new client instead."
              : "No clients match that search."}
          </p>
        ) : (
          filtered.map((c) => (
            <button
              key={c._id}
              disabled={isAdding}
              onClick={() => onAddExisting(c)}
              className="flex w-full items-center justify-between border-b border-[#F2F4F7] px-4 py-3 text-left last:border-b-0 hover:bg-[#F9FAFB] disabled:opacity-60"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-[#111827]">
                  {c.name || c.email}
                </p>
                <p className="truncate text-[12px] text-[#98A2B3]">
                  {c.email}
                  {c.description ? ` · ${c.description}` : ""}
                </p>
              </div>
              <span className="ml-3 flex shrink-0 items-center gap-2">
                {c.status === "pending" && (
                  <span className="rounded-full bg-[#FEF2D4] px-2 py-[2px] text-[10px] text-[#7C4A10]">
                    PENDING
                  </span>
                )}
                <span className="rounded-[6px] bg-[#EEF0FF] px-2.5 py-1 text-xs font-semibold text-[#4F46E5]">
                  Add
                </span>
              </span>
            </button>
          ))
        )}
      </div>

      <div className="mt-5 flex justify-end">
        <button
          onClick={() => setMode("ask")}
          className="h-10 rounded-[10px] border border-[#D0D5DD] bg-white px-5 text-sm font-medium text-[#344054] hover:bg-[#F9FAFB]"
        >
          Back
        </button>
      </div>
    </ChoiceDialog>
  );
};

export default AddClientQuestionModal;
